/**
 * Offline class demo — localStorage fixtures, no auth / API.
 * Mirrors product: student RSVP reserves a seat; staff Mark Present debits a credit.
 * Student: /demo/class · Instructor: /demo/class/dashboard
 */

const STORAGE_KEY = "ln_class_demo_state_v2";

export const CLASS_DEMO_PATH = "/demo/class?demo=true";
export const CLASS_DEMO_DASHBOARD_PATH = "/demo/class/dashboard?demo=true";
export const CLASS_DEMO_ID = "demo-class-level-2";

/** Starting pack balance for the demo student (matches seeded credit packs). */
export const DEMO_CLASS_PACK_CREDITS = 5;

function nextMondayAt(hour, minute) {
  const d = new Date();
  const day = d.getDay(); // 0 Sun … 6 Sat
  const daysUntilMon = day === 0 ? 1 : day === 1 ? 0 : 8 - day;
  d.setDate(d.getDate() + daysUntilMon);
  d.setHours(hour, minute, 0, 0);
  if (d.getTime() < Date.now() - 60 * 60 * 1000) {
    d.setDate(d.getDate() + 7);
  }
  return d.toISOString();
}

function addDaysIso(iso, days) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

const SESSION_A = nextMondayAt(19, 0);
const SESSION_B = addDaysIso(SESSION_A, 2);
const SESSION_C = addDaysIso(SESSION_A, 7);

export const DEMO_CLASS = {
  id: CLASS_DEMO_ID,
  name: "Salsa Level 2",
  school_name: "Sala Caracas Dance Academy",
  description: "For dancers familiar with the basic step.",
  style: "Salsa",
  level: "Improvers",
  location: "Studio A, Kreuzberg",
  instructors: ["Lina", "Tomas"],
  capacity: 15,
  recurrence: { days: ["Mon", "Wed"], time: "19:00" },
  products: [
    { id: "demo-pack-5", name: "5-class pack", credits: 5, price_label: "75€" },
    { id: "demo-drop-in", name: "Drop-in", credits: 1, price_label: "18€" },
  ],
  instances: [
    {
      id: "demo-class-inst-1",
      datetime: SESSION_A,
      status: "SCHEDULED",
    },
    {
      id: "demo-class-inst-2",
      datetime: SESSION_B,
      status: "SCHEDULED",
    },
    {
      id: "demo-class-inst-3",
      datetime: SESSION_C,
      status: "SCHEDULED",
    },
  ],
};

const FIXTURE_ROSTER = [
  { id: "demo-cr-carla", name: "Carla", role: "Follower", status: "CONFIRMED", redemption_via: "CREDIT" },
  { id: "demo-cr-marco", name: "Marco", role: "Leader", status: "ATTENDED", redemption_via: "CREDIT" },
  { id: "demo-cr-yuki", name: "Yuki", role: "Both", status: "CONFIRMED", redemption_via: "TIME_PASS" },
  { id: "demo-cr-sofia", name: "Sofia", role: "Follower", status: "CONFIRMED", redemption_via: "CREDIT" },
  { id: "demo-cr-jamal", name: "Jamal", role: "Leader", status: "CONFIRMED", redemption_via: "HOST" },
  { id: "demo-cr-elena", name: "Elena", role: "Follower", status: "ATTENDED", redemption_via: "CREDIT" },
  { id: "demo-cr-ravi", name: "Ravi", role: "Leader", status: "CONFIRMED", redemption_via: "CREDIT" },
  { id: "demo-cr-naomi", name: "Naomi", role: "Follower", status: "CONFIRMED", redemption_via: "TIME_PASS" },
  { id: "demo-cr-helena", name: "Helena", role: "Follower", status: "CONFIRMED", redemption_via: "CREDIT" },
  { id: "demo-cr-andre", name: "Andre", role: "Leader", status: "CONFIRMED", redemption_via: "CREDIT" },
  { id: "demo-cr-vera", name: "Vera", role: "Follower", status: "CONFIRMED", redemption_via: "HOST" },
  { id: "demo-cr-felix", name: "Felix", role: "Both", status: "CONFIRMED", redemption_via: "CREDIT" },
];

function emptyState() {
  return {
    version: 2,
    my_rsvp: null,
    product_id: "demo-pack-5",
    credits_remaining: DEMO_CLASS_PACK_CREDITS,
    roster: FIXTURE_ROSTER.map((r) => ({ ...r })),
    check_in_times: {},
  };
}

export function readClassDemoState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== 2) return emptyState();
    return {
      ...emptyState(),
      ...parsed,
      roster: Array.isArray(parsed.roster) ? parsed.roster : FIXTURE_ROSTER.map((r) => ({ ...r })),
      check_in_times: parsed.check_in_times || {},
      credits_remaining:
        parsed.credits_remaining != null ? parsed.credits_remaining : DEMO_CLASS_PACK_CREDITS,
    };
  } catch {
    return emptyState();
  }
}

export function writeClassDemoState(state) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  return state;
}

export function activateClassDemoSession(fromQuery) {
  if (!fromQuery) return;
  try {
    document.cookie = "rsvp_demo=1; path=/; max-age=86400; SameSite=Lax";
  } catch {
    // ignore
  }
}

export function resetClassDemo({ redirectTo = CLASS_DEMO_PATH } = {}) {
  try {
    localStorage.removeItem(STORAGE_KEY);
    // Clear legacy v1 key if present
    localStorage.removeItem("ln_class_demo_state_v1");
  } catch {
    // ignore
  }
  window.location.assign(redirectTo);
}

/** Student RSVP — reserves seat; does not debit credits (product rule). */
export function saveClassDemoRsvp({ name, role }, state = readClassDemoState()) {
  const dancerId = "demo-user-class-student";
  const myRsvp = {
    id: "demo-cr-me",
    name: String(name || "").trim() || "You",
    role: role || "Follower",
    status: "CONFIRMED",
    redemption_via: "CREDIT",
    organizer_id: dancerId,
  };
  const withoutMe = (state.roster || []).filter((r) => r.id !== myRsvp.id);
  const next = {
    ...state,
    my_rsvp: myRsvp,
    product_id: state.product_id || "demo-pack-5",
    credits_remaining:
      state.credits_remaining != null ? state.credits_remaining : DEMO_CLASS_PACK_CREDITS,
    roster: [myRsvp, ...withoutMe],
  };
  return writeClassDemoState(next);
}

/** Staff Mark Present — sets ATTENDED and debits 1 credit for CREDIT redemptions. */
export function markClassDemoPresent(rsvpId, state = readClassDemoState()) {
  const now = new Date();
  const timeLabel = now.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const target = (state.roster || []).find((r) => r.id === rsvpId);
  const alreadyAttended = target?.status === "ATTENDED";
  const shouldDebit =
    !alreadyAttended && target?.redemption_via === "CREDIT" && rsvpId === state.my_rsvp?.id;

  const roster = (state.roster || []).map((r) =>
    r.id === rsvpId ? { ...r, status: "ATTENDED" } : r,
  );
  const myRsvp =
    state.my_rsvp?.id === rsvpId ? { ...state.my_rsvp, status: "ATTENDED" } : state.my_rsvp;

  let credits = state.credits_remaining;
  if (shouldDebit && credits != null) {
    credits = Math.max(0, credits - 1);
  }

  const next = {
    ...state,
    my_rsvp: myRsvp,
    roster,
    credits_remaining: credits,
    check_in_times: { ...(state.check_in_times || {}), [rsvpId]: timeLabel },
  };
  return writeClassDemoState(next);
}

export function addClassDemoWalkIn({ name, role }, state = readClassDemoState()) {
  const id = `demo-cr-walkin-${Date.now()}`;
  const walkIn = {
    id,
    name: String(name || "").trim() || "Walk-in",
    role: role || "Follower",
    status: "ATTENDED",
    redemption_via: "HOST",
    source: "HOST",
  };
  const timeLabel = new Date().toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const next = {
    ...state,
    roster: [...(state.roster || []), walkIn],
    check_in_times: { ...(state.check_in_times || {}), [id]: timeLabel },
  };
  return writeClassDemoState(next);
}

export function classDemoGuestCounts(roster = []) {
  const coming = roster.filter((r) => r.status !== "CANCELLED");
  return {
    total: coming.length,
    leaders: coming.filter((r) => r.role === "Leader").length,
    followers: coming.filter((r) => r.role === "Follower").length,
    both: coming.filter((r) => r.role === "Both").length,
  };
}
